import { createStyles, Theme } from "@material-ui/core";
import { COLORS } from "../../config/theme/baseTheme";

// Explicitly define CSS property types
type PositionProperty = "static" | "relative" | "absolute" | "fixed" | "sticky";
type TextAlignProperty =
  | "left"
  | "right"
  | "center"
  | "justify"
  | "initial"
  | "inherit";
type FlexDirectionProperty =
  | "row"
  | "row-reverse"
  | "column"
  | "column-reverse"
  | "initial"
  | "inherit";
type TextTransformProperty =
  | "none"
  | "capitalize"
  | "uppercase"
  | "lowercase"
  | "initial"
  | "inherit";

type FlexWrapProperty =
  | "nowrap"
  | "wrap"
  | "wrap-reverse"
  | "initial"
  | "inherit";

type FloatProperty =
  | "left"
  | "right"
  | "none"
  | "inline-start"
  | "inline-end"
  | "inherit"
  | "initial"
  | "unset";

export const fontStyles = {
  heading: {
    fontSize: 18,
    fontWeight: 500,
  },
  subHeading: {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: "17px",
  },
  content: {
    fontSize: 12,
    fontWeight: 600,
  },
  normalText: {
    fontSize: 14,
    lineHeight: "17px",
  },
  smallText: {
    fontSize: 12,
    lineHeight: "15px",
  },
  tinyText: {
    fontSize: 10,
    lineHeight: "10px",
  },
};

export const addressStyles = (theme: Theme) => ({
  address: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.primary.main,
    lineHeight: "14px",
    paddingRight: 3,
  },
  addressLogo: {
    width: 12,
    height: 12,
    transform: "rotate(0deg)",
    transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  },
  addressMapContainer: {
    height: 243,
    border: `1px solid ${COLORS.SILVER}`,
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(1),
    backgroundColor: COLORS.SILVER,
  },
});

export const chipContainerStyles = (theme: Theme) => ({
  "& .chipContainer": {
    alignItems: "center",
    height: "100%",
    "& .chipItem": {
      padding: theme.spacing(0, 1.5),
      "& p": {
        fontWeight: 500,
        color: COLORS.MINE_SHAFT,
      },
      "&.name": {
        "& p": {
          fontSize: 14,
        },
      },
      "&.count": {
        marginLeft: "auto",
        "& p": {
          fontSize: 24,
          fontWeight: 600,
        },
      },
    },
  },
})

export const chipFilterStyles = (theme: Theme) => ({
  borderRadius: "30px",
  boxShadow: "0px 4px 48px rgb(0 0 0 / 8%)",
  backgroundColor: "#FFFFFF",
  cursor: "pointer",
  "&:last-child": {
    marginRight: 0,
  },
  "&.firstItem": {
    boxShadow: `0px 0px 10px 4px ${COLORS.BOX_SHADOW_PRIMARY}`,
    border: `2px solid ${theme.palette.primary.main}`,
  },
  "&.active": {
    background: COLORS.PRIMARY_MAIN,
    border: "none",
    boxShadow: "none",
    "& .chipItem p": {
      color: COLORS.WHITE,
    },
  },
  ...chipContainerStyles(theme) 
});

export const detailsHeaderStyles = (theme: Theme) => ({
  container: {
    flexDirection: "column",
    backgroundColor: COLORS.TRANSPARENT_BLUE,
    padding: "18px 16px",
    borderRadius: theme.spacing(1),
  },
  item: {
    paddingBottom: theme.spacing(2),
    "&:last-child": {
      paddingBottom: 0,
    },
    "& .title": {
      ...fontStyles.subHeading,
    },
    "& .editIcon": {
      display: "flex",
      justifyContent: "flex-end",
    },
    "& .subSection": {
      paddingTop: "16px",
      display: "flex",
    },
    "& .subSectionTitle": {
      fontSize: "14px",
      fontWeight: 600,
    },
    "& .subSectionContent": {
      fontSize: "14px",
      fontWeight: 500,
      paddingLeft: "8px",
    },
    "& .headerItemContainer": {
      alignItems: "center",
      flexWrap: "nowrap",
    },
    "& .contactItemContainer": {
      alignItems: "center",
      flexWrap: "nowrap",
    },
    "& p": {
      ...fontStyles.normalText,
    },
    "& .name": {
      fontWeight: 600,
    },
    "& .contactMobile": {
      marginRight: theme.spacing(5),
    },
    "& .logo": {
      width: 24,
      height: 24,
    },
    "& .contact": {
      paddingLeft: theme.spacing(1),
      "&.mobile": {
        paddingLeft: theme.spacing(0.25),
      },
      "& a": {
        ...fontStyles.normalText,
        fontWeight: 600,
        textDecoration: "none",
        color: theme.palette.text.primary,
      },
    },
  },
});

export const geocodedAddressStyles = (theme: Theme) => ({
  "& .addressColItem": {
    alignSelf: "flex-start",
  },
  "& .geoLocAddressContainer": {
    cursor: "pointer",
    "& .address": {
      ...addressStyles(theme).address,
    },
    "& .addressLogo": {
      ...addressStyles(theme).addressLogo,
      "&.open": {
        transform: "rotate(180deg)",
      },
    },
  },
  "& .addressMapContainer": {
    ...addressStyles(theme).addressMapContainer,
  },
});

export const headingContainerStyles = () => ({
  "& .headingContainer": {
    flexDirection: "column",
    "& .titleHeading": {
      fontSize: 18,
      fontWeight: 500,
      "& .content": {
        fontWeight: 600,
      },
    },
    "& .titleContent": {
      fontSize: 12,
      fontWeight: 600,
      "&.link": {
        cursor: "pointer",
      },
    },
  },
});

export const deliveryProofSectionStyles = (theme: Theme) => ({
  "& .deliveryProofSection": {
    paddingBottom: theme.spacing(1),
    "& .content": {
      width: "100%",
      "& .deliveryProofContainer": {
        flexDirection: "column",
        "& .deliveryProofItem": {
          paddingBottom: theme.spacing(1.5),
          "&:last-child": {
            paddingBottom: 0,
          },
          "& .imagesContainer": {
            flexWrap: "nowrap",
            "& .imagesItem": {
              marginRight: theme.spacing(1.5),
              width: "50%",
              height: 145,
              backgroundColor: theme.palette.secondary.main,
              borderRadius: theme.spacing(1),
              padding: theme.spacing(2),
              "&:last-child": {
                marginRight: 0,
              },
              "& .imageItemContainer": {
                flexDirection: "column",
                "& .imageItem": {
                  paddingBottom: theme.spacing(2),
                  "& .heading": {
                    fontSize: 14,
                    fontWeight: 500,
                  },
                  "& .allImagesContainer": {
                    flexWrap: "nowrap",
                    overflow: "auto",
                    "& .viewerContainer": {
                      display: "flex",
                    },
                    "&.sign": {
                      justifyContent: "space-around",
                    },
                    "& img": {
                      width: "auto",
                      height: 64,
                      cursor: "pointer",
                      marginRight: theme.spacing(1.5),
                      "&:last-child": {
                        marginRight: 0,
                      },
                    },
                  },
                },
              },
            },
          },
          "& .commentsContainer": {
            flexDirection: "column",
            padding: theme.spacing(1),
            backgroundColor: theme.palette.secondary.main,
            borderRadius: theme.spacing(1),
            "& .commentsItem": {
              paddingBottom: theme.spacing(0.75),
              "& p": {
                fontWeight: 500,
                fontSize: 12,
              },
              "& .heading": {
                fontSize: 14,
              },
            },
          },
        },
      },
    },
  },
});

export const tagStyles = (theme: Theme) => ({
  base: {
    color: COLORS.WHITE,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    lineHeight: "14px",
    backgroundColor: COLORS.BLACK,
    fontWeight: 600,
    textTransform: "uppercase" as TextTransformProperty,
    fontSize: 12,
  },
  colorVariants: {
    PICTON_BLUE: { backgroundColor: COLORS.PICTON_BLUE },
    CREAM_CAN: { backgroundColor: COLORS.CREAM_CAN },
    VISTA_BLUE: { backgroundColor: COLORS.VISTA_BLUE },
    SEA_GREEN: { backgroundColor: COLORS.SEA_GREEN },
    GOLDEN_BELL: { backgroundColor: COLORS.GOLDEN_BELL },
    SCAMPI: { backgroundColor: COLORS.SCAMPI },
    BLUE: { backgroundColor: COLORS.PRIMARY_MAIN },
    SEA_GREEN2: { backgroundColor: COLORS.SEA_GREEN2 },
    LUCKY: { backgroundColor: COLORS.LUCKY },
    DUSTY_GRAY: { backgroundColor: COLORS.DUSTY_GRAY },
    JUNGLE_GREEN: { backgroundColor: COLORS.JUNGLE_GREEN },
    BLACK: { backgroundColor: COLORS.BLACK },
  },
});



// Restructure timeline styles to avoid nested objects
export const timelineBaseStyles = () => ({
  padding: "0 !important",
  margin: 0,
});

export const timelineItemStyles = () => ({
  minHeight: 50,
  "&:last-child": {
    minHeight: "auto",
  },
  "&:before": {
    content: "none",
  },
});

export const timelineDotStyles = () => ({
  padding: 0,
  margin: 0,
  border: "none",
  boxShadow: "none",
  backgroundColor: "transparent",
  "& .dotIcon": {
    width: 18,
    height: 18,
    padding: 5,
    "&.active": {
      padding: 0,
    },
  },
});

export const timelineConnectorStyles = () => ({
  backgroundColor: COLORS.TRANSPARENT,
  borderLeft: `1px dashed ${COLORS.SILVER}`,
});

export const timelineContentStyles = (theme: Theme) => ({
  padding: 0,
  paddingLeft: theme.spacing(1),
  "& .heading": {
    paddingTop: 1,
    fontSize: 14,
    lineHeight: "14px",
    fontWeight: 600,
    color: theme.palette.primary.main,
    "&.error": {
      color: COLORS.DARK_RED,
      "&.active": {
        paddingBottom: 17,
      },
    },
    "& .dateTime": {
      ...fontStyles.smallText,
      fontWeight: 500,
      color: COLORS.DOVE_GRAY_DARK,
      paddingLeft: theme.spacing(0.5),
      verticalAlign: "bottom",
    },
    "& .metadataContainer": {
                paddingBottom: theme.spacing(1.5),
                "& .title": {
                  fontSize: 12,
                  lineHeight: "10px",
                  paddingTop: theme.spacing(1),
                  paddingBottom: theme.spacing(1),
                  fontWeight: 500,
                  color: COLORS.COPY,
                },
                "& .description": {
                  fontSize: 10,
                  lineHeight: "10px",
                  color: COLORS.DOVE_GRAY_LIGHT,
                },
              },
  },
  "& .metadataContainer": {
    paddingBottom: theme.spacing(1.5),
    "& .title": {
      fontSize: 12,
      lineHeight: "10px",
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      fontWeight: 500,
      color: COLORS.COPY,
    },
    "& .description": {
      fontSize: 10,
      lineHeight: "10px",
      color: COLORS.DOVE_GRAY_LIGHT,
    },
  },
});

export const timelineStyles = (theme: Theme) => ({
  timeline: {
    ...timelineBaseStyles(),
    "& .timelineItem": {
      ...timelineItemStyles(),
      "& .timelineDot": {
        ...timelineDotStyles(),
      },
      "& .primaryConnector": {
        ...timelineConnectorStyles(),
      },
      "& .timelineContent": {
        ...timelineContentStyles(theme),
      },
    },
  },
});

export const pdfStyles = () => ({
  docStyle: {
    overflow: "scroll",
    height: "890px",
  },
  header: {
    position: "absolute" as PositionProperty,
    backgroundColor: "#393939",
    height: "56px",
    width: "100%",
    zIndex: 1,
  },
  root: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  name: {
    fontSize: 14,
    fontWeight: 600,
    color: COLORS.WHITE,
    lineHeight: "17px",
    display: "flex",
    alignContent: "center",
    alignItems: "center",
  },
  scaleNo: {
    backgroundColor: "#000408",
    width: "44px",
    height: "24px",
    padding: "6px",
    color: "white",
  },
  body: {
    borderRadius: "8px",
    "& .react-pdf__Page": {
      marginTop: "56px",
      "& .react-pdf__Page__canvas": {
        margin: "0 auto",
        height: "100% !important",
        overflow: "auto",
        minWidth: "1064px",
      },
    },
    "& .react-pdf__Page__annotations.annotationLayer": {
      padding: "8px",
    },
  },
});

export const noPdfStyles = () => ({
  modal: {
    border: "none",
    minWidth: "800px",
    minHeight: "500px",
    maxHeight: "890px",
    backgroundColor: COLORS.SECONDARY_LIGHT,
    position: "absolute" as PositionProperty,
    top: "40%",
    left: "50%",
    transform: "translate(-40%, -50%)",
    padding: "24px",
    textAlign: "center" as TextAlignProperty,
    borderRadius: "8px",
  },
  closeIcon: {
    position: "absolute" as PositionProperty,
    right: "-20px",
    top: "-21px",
    width: "40px",
    height: "40px",
    zIndex: 1,
    cursor: "pointer",
  },
  message: {
    fontSize: "18px",
    fontWeight: 500,
  },
});

export const modalStyles = () => ({
  dialogPaper: {
    margin: 0,
    marginLeft: "auto",
    maxHeight: "none",
    height: "100vh",
    width: 600,
  },
  pdf: pdfStyles(),
  noPdf: noPdfStyles(),
});

export const excelDownloadStyles = (theme: Theme) => ({
  excelDownloadStatus: {
    background: COLORS.INFO_BLACK,
    borderRadius: "8px 8px 0px 0px",
    position: "fixed" as PositionProperty,
    bottom: "12px",
    height: 48,
    width: 336,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  excelDownloadStatusText: {
    color: COLORS.WHITE,
    fontSize: 14,
    lineHeight: "17px",
    fontWeight: 500,
    fontFamily: "Montserrat",
  },
  checkedIconStyle: {
    width: 33,
    height: 33,
    padding: "6px 0px 0px 16px",
  },
  close: {
    color: COLORS.WHITE,
  },
  closeIcon: {
    width: 16,
    height: 16,
  },
  excelDownloadError: {
    background: COLORS.LIGHT_RED,
    borderRadius: "8px 8px 0px 0px",
    position: "fixed" as PositionProperty,
    bottom: "23px",
    height: 48,
    width: 336,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  button: {
    margin: theme.spacing(0.5),
    "&:last-child": {
      marginRight: theme.spacing(0),
    },
  },
  colSettings: {
    float: "right" as FloatProperty,
    marginTop: -3,
  },
  buttonContainer: {
    marginLeft: "auto",
    "& .clearAllFilterBtn": {
      fontSize: 14,
      fontWeight: 600,
      marginRight: theme.spacing(2),
    },
  },
});
export const gridListStyles = (theme: Theme) => ({
  root: {
    padding: "0px 12px 0px 12px",
    height: "100%",
  },
  container: {
    flexDirection: "column" as FlexDirectionProperty,
    height: "100%",
    "& .userItem": {
      width: "100%",
      "&.heading": {
        paddingBottom: theme.spacing(2.5),
        "& p": {
          fontSize: 24,
          color: COLORS.BLACK,
          fontWeight: 600,
        },
      },
      "&.content": {
        backgroundColor: COLORS.WHITE,
        borderRadius: theme.spacing(1),
        height: "calc(100vh - 172px)",
        paddingTop: theme.spacing(1.5),
        "& .hubIdLink": {
          cursor: "pointer",
          color: theme.palette.primary.main,
          fontWeight: 600,
          fontSize: 12,
        },
        "& .serviceType": {
          color: "#2D2D2D",
          fontWeight: 500,
          fontSize: 12,
          textTransform: "capitalize",
        },
      },
    },
  },
  statusHeaderContainer: {
    "& .tag": {
      color: COLORS.WHITE,
      borderRadius: theme.spacing(0.5),
      padding: theme.spacing(0.5, 1),
      lineHeight: "14px",
      backgroundColor: COLORS.BLACK,
      fontWeight: 600,
      textTransform: "uppercase" as TextTransformProperty,
      fontSize: 12,
      "&.yellow": {
        color: COLORS.GOLDEN_BELL,
        backgroundColor: COLORS.GOLDEN_YELLOW_LIGHT,
      },
      "&.green": {
        color: COLORS.PERSIAN_GREEN_DARK,
        backgroundColor: COLORS.PERSIAN_GREEN_LIGHT,
      },
      "&.red": {
        color: COLORS.DARK_RED,
        backgroundColor: COLORS.LIGHT_RED,
      },
      "&.gray": {
        color: COLORS.GRAY_DARK,
        backgroundColor: COLORS.ALTO,
      },
      "&.black": {
        backgroundColor: COLORS.BLACK,
        color: COLORS.WHITE,
      },
    },
  },
  actionsHeader: {
    alignItems: "center",
    "& .actionItem": {
      cursor: "pointer",
      "& .icon": {
        padding: theme.spacing(0.25),
      },
    },
  },
});

export const sharedStyles = (theme: Theme) =>
  createStyles({
    ...fontStyles,
    ...addressStyles(theme),
    detailsHeader: detailsHeaderStyles(theme),
    tag: tagStyles(theme).base,
    tagVariants: tagStyles(theme).colorVariants,
    // Flat timeline structure
    timelineBase: timelineBaseStyles(),
    timelineItem: timelineItemStyles(),
    timelineDot: timelineDotStyles(),
    timelineConnector: timelineConnectorStyles(),
    timelineContent: timelineContentStyles(theme),
    // Modal styles
    modal: modalStyles(),
    gridList: gridListStyles(theme),
  });

export const useSideModalCloseOpenStyles = () => ({
  openX: {
    transform: "scaleX(1)",
    height: 18,
    width: 20,
    color: COLORS.COPY,
  },
  closeX: {
    transform: "scaleX(-1)",
    height: 18,
    width: 20,
    color: COLORS.COPY,
  },
  openY: {
    transform: "scaleY(1)",
    height: 18,
    width: 20,
    color: COLORS.COPY,
  },
  closeY: {
    transform: "scaleY(-1)",
    height: 18,
    width: 20,
    color: COLORS.COPY,
  },
});

export const useDialogPaperPropsRootStyle = (theme: Theme) => ({
  publishDialogContentContainer: {
    flexDirection: "column" as FlexDirectionProperty,
    flexWrap: "nowrap" as FlexWrapProperty,
    "& .contentItem": {
      "& p": {
        textAlign: "center",
        lineHeight: 1.2,
      },
      "&.heading": {
        padding: theme.spacing(0, 15.75, 5),
        "& p": {
          fontSize: 24,
          fontWeight: 600,
        },
      },
      "&.desc": {
        padding: theme.spacing(0, 2.5),
        "& p": {
          fontSize: 18,
          fontWeight: 400,
          "& .count": {
            fontWeight: 600,
          },
        },
      },
    },
  },
  publishDialogPaperPropsRoot: {
    width: 600,
    height: 350,
    margin: "auto",
    borderRadius: theme.spacing(1),
  },
  deleteDialogPaperPropsRoot: {
    width: 600,
    height: 350,
    margin: "auto",
    borderRadius: theme.spacing(1),
  },
  hubSelectionDialogPaperPropsRoot: {
    width: 500,
    height: 300,
    margin: "auto",
    borderRadius: theme.spacing(1),
  },
});

export const usePublishRouteDialogStyle = (theme: Theme) => ({
  hubSelectionDialogTitle: {
    fontSize: 20,
    fontWeight: 600,
  },
  hubSelectionDialogContentContainer: {
    flexDirection: "column" as FlexDirectionProperty,
    flexWrap: "nowrap" as FlexWrapProperty,
    "& .contentItem": {
      "&.heading": {
        "& p": {
          fontSize: 14,
          fontWeight: 400,
        },
      },
      "&.hub": {
        paddingTop: theme.spacing(3.75),
      },
    },
  },
  publishDialogButtonsContainer: {
    justifyContent: "center",
    paddingBottom: theme.spacing(8.25),
    "& .dialogBtn": {
      "&.secondary": {
        paddingLeft: theme.spacing(8),
        paddingRight: theme.spacing(8),
        marginRight: theme.spacing(1),
      },
      "&.primary": {
        paddingLeft: theme.spacing(7.25),
        paddingRight: theme.spacing(7.25),
        marginLeft: theme.spacing(1),
      },
    },
  },
  deleteDialogButtonsContainer: {
    justifyContent: "center",
    paddingBottom: theme.spacing(8.25),
    "& .dialogBtn": {
      "&.secondary": {
        paddingLeft: theme.spacing(8),
        paddingRight: theme.spacing(8),
        marginRight: theme.spacing(1),
      },
      "&.primary": {
        paddingLeft: theme.spacing(7.25),
        paddingRight: theme.spacing(7.25),
        marginLeft: theme.spacing(1),
      },
    },
  },
  hubSelectionDialogButtonsContainer: {
    justifyContent: "flex-end",
    paddingBottom: theme.spacing(7),
    "& .dialogBtn": {
      paddingLeft: theme.spacing(4.5),
      paddingRight: theme.spacing(4.5),
      "&.secondary": {
        marginRight: theme.spacing(1),
      },
      "&.primary": {
        marginLeft: theme.spacing(1),
      },
    },
  },
  deleteDialogContentContainer: {
    flexDirection: "column" as FlexDirectionProperty,
    flexWrap: "nowrap" as FlexWrapProperty,
    "& .contentItem": {
      "& p": {
        textAlign: "center",
        lineHeight: 1.2,
      },
      "&.heading": {
        padding: theme.spacing(0, 15.75, 5),
        "& p": {
          fontSize: 24,
          fontWeight: 600,
        },
      },
      "&.desc": {
        padding: theme.spacing(0, 2.5),
        "& p": {
          fontSize: 18,
          fontWeight: 400,
          "& .count": {
            fontWeight: 600,
          },
        },
      },
    },
  },
  publishErrorPopupContainer: {
    position: "absolute" as PositionProperty,
    bottom: 0,
    height: 100,
    backgroundColor: "#FEECEC",
    flexDirection: "column" as FlexDirectionProperty,
    flexWrap: "nowrap" as FlexWrapProperty,
    padding: theme.spacing(2),
    "& .titleBlock": {
      "& .titleContainer": {
        alignItems: "center",
        justifyContent: "space-between",
        "& .titleItem": {
          "& .title": {
            fontSize: 14,
            fontWeight: 600,
          },
          "& .closeBtn": {
            padding: theme.spacing(0),
          },
        },
      },
    },
    "& .contentBlock": {
      flexGrow: 1,
      overflow: "auto",
      "& .contentContainer": {
        flexDirection: "column" as FlexDirectionProperty,
        "& .contentItem": {
          paddingBottom: theme.spacing(1.25),
          "&:last-child": {
            paddingBottom: theme.spacing(0),
          },
          "& .content": {
            alignItems: "center",
            "& .dotIcon": {
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#F34041",
              marginRight: theme.spacing(2),
            },
            "& .text": {
              fontSize: 14,
              fontWeight: 500,
            },
          },
        },
      },
    },
  },
});

export const detailsHeaderStyle = (theme: Theme) => ({
  "& .detailsHeader": {
    flexDirection: "column",
    backgroundColor: COLORS.TRANSPARENT_BLUE,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    "& .detailsHeaderItem": {
      paddingBottom: theme.spacing(1),
      "&:last-child": {
        paddingBottom: 0,
      },
      "& .headerItemContainer": {
        alignItems: "center",
        flexWrap: "nowrap",
      },
      "& .contactItemContainer": {
        alignItems: "center",
        flexWrap: "nowrap",
      },
      "& p": {
        lineHeight: "17px",
        fontSize: 14,
      },
      "& .name": {
        fontWeight: 600,
      },
      "&.geocodedAddress": {
        paddingBottom: theme.spacing(2),
        ...geocodedAddressStyles(theme),
      },
      "& .contactMobile": {
        marginRight: theme.spacing(5),
      },
      "& .logo": {
        width: 24,
        height: 24,
      },
      "& .contact": {
        "& a": {
          lineHeight: "17px",
          fontSize: 14,
          fontWeight: 600,
          textDecoration: "none",
          color: theme.palette.text.primary,
        },
        paddingLeft: theme.spacing(1),
        "&.mobile": {
          paddingLeft: theme.spacing(0.25),
        },
      },
    },
  },
})

export const detailsContentStyle = (theme: Theme) => ({
  "& .detailsContent": {
    flexDirection: "column",
    flexWrap: "nowrap",
    padding: theme.spacing(3, 0, 2, 0),
    "& .contentRow": {
      paddingBottom: theme.spacing(3),
      "&.hubDetails": {
        paddingBottom: theme.spacing(1),
      },
      "&:last-child": {
        paddingBottom: 0,
      },
      "& .contentColumn": {
        flex: "2 0 0",
        "&:last-child": {
          flex: "1 0 0",
        },
        "& p": {
          fontSize: 14,
          lineHeight: "17px",
        },
        "& .label": {
          paddingBottom: theme.spacing(1),
        },
        "& .content": {
          fontWeight: 600,
        },
      },
    },
  },
})

export const detailsContainer = (theme: Theme) => ({
  ...detailsHeaderStyle(theme),
  ...detailsContentStyle(theme)
})

export const tabularSectionCratesStyles = (theme: Theme) => ({
  "& .itemsContainer .item .itemContainer .itemColumn.item1 p": {
    fontWeight: 600,
  },
  "& .itemsContainer .item .itemContainer .itemColumn.item1 .header p": {
    fontWeight: 500,
  },
  "& .crateBarcode": {
    width: 40,
    height: 15,
    marginLeft: theme.spacing(3),
  },
  "& .crateStageLocationIcon": {
    width: 16,
    height: 16,
    marginRight: theme.spacing(1),
  },
});



export const paymentContainerStyles = (theme: Theme) => ({
    "& .paymentContainer": {
      padding: 16,
      paddingTop: 13,
      "& .totalDivider": {
        margin: "11px 0px",
      },
      "& .paymentItem": {
        "&:last-child p": {
          fontWeight: 600,
        },
        padding: "5px 0",
        "& .paymentItemColumn": {
          "& p": {
            fontSize: 14,
            lineHeight: "17px",
          },
          "& .vat": {
            fontWeight: 400,
            paddingLeft: theme.spacing(0.25),
          },
          "&.right": {
            marginLeft: "auto",
          },
        },
      },
    },
})

export const contentStyle = (theme: Theme) => ({
   width: "100%",
  "& .itemsContainer": {
    flexWrap: "nowrap",
    "& .item": {
      padding: theme.spacing(2, 0),
      borderBottom: `1px solid ${theme.palette.secondary.dark}`,
      "&:first-child": {
        padding: 0,
      },
      "&:last-child": {
        borderBottom: "none",
      },
      "& .itemContainer": {
        padding: theme.spacing(0, 2),
        "&.items": {
          backgroundColor: COLORS.SECONDARY_MAIN,
          padding: theme.spacing(2),
        },
        "& p": {
          fontSize: 14,
          lineHeight: "17px",
          fontWeight: 600,
        },
        "&.header p": {
          color: COLORS.DOVE_GRAY,
          fontWeight: 500,
        },
        "& .itemColumn": {
          padding: theme.spacing(0),
          "&.item1": {
            "& p": {
              fontWeight: 400,
              textTransform: "capitalize",
            },
            "& .rejectedReason": {
              fontSize: 10,
              color: COLORS.ALIZARIN_CRIMSON,
            },
            "& .viewOriginalLink": {
              fontSize: 9,
              color: COLORS.COPY,
              cursor: "pointer",
              paddingRight: 6,
            },
            "& .originalName": {
              fontSize: 14,
              lineHeight: "17px",
              fontWeight: 400,
            },
          },
          "&.item2": {
            "& p": {
              textAlign: "center",
              "& .green": {
                color: COLORS.PERSIAN_GREEN,
              },
            },
          },
          "&.item3": {
            "& p": {
              textAlign: "right",
            },
          },
        },
      },
    },
  },
})

export const tabularSectionItemContentStyles = (theme: Theme) => ({
 ...contentStyle(theme),
  ...paymentContainerStyles(theme),
});

export const rootSectionWithContainer = (theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
    height: "100%",
  },
  container: {
    flexDirection: "column" as FlexDirectionProperty,
    "& .planningItem": {
      width: "100%",
      "& .MuiTab-root": {
        marginRight: 44,
      },
      "&.heading": {
        "& p": {
          fontSize: 20,
          lineHeight: "30px",
          color: COLORS.PRIMARY_MAIN,
          fontFamily: "Montserrat",
          fontWeight: 600,
        },
      },
      "&.content": {
        backgroundColor: COLORS.WHITE,
        borderRadius: theme.spacing(1),
        height: "calc(100vh - 172px)",
        "& .tabPanel": {
          height: "calc(100% - 56px)",
          padding: 0,
          "&.routes": {
            position: "relative",
          },
        },
      },
    },
  },
});

export const orderTag = (theme: Theme) => ({
  background: "#FFFFFF",
  border: "1px solid #319E60",
  borderRadius: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1),
  fontWeight: 600,
  fontSize: 12,
  color: "#319E60",
  lineHeight: "14px",
  height: "24px",
  textTransform: "uppercase",
});

export const orderStatusTagVariants = {
  "&.GOLDEN_BELL": {
    backgroundColor: COLORS.GOLDEN_BELL,
    color: COLORS.GOLDEN_BELL,
  },
  "&.MINSK": {
    backgroundColor: COLORS.SCAMPI,
    color: COLORS.SCAMPI,
  },
  "&.READY_BLUE": {
    backgroundColor: COLORS.READY_BLUE,
    color: COLORS.READY_BLUE,
  },
  "&.SCAMPI": {
    backgroundColor: COLORS.SCAMPI,
    color: COLORS.SCAMPI,
  },
  "&.INFO_BLUE": {
    backgroundColor: COLORS.INFO_BLUE,
    color: COLORS.INFO_BLUE,
  },
  "&.MINSK_DARK": {
    backgroundColor: COLORS.MINSK_DARK,
    color: COLORS.MINSK_DARK,
  },
  "&.DRIFT_WOOD": {
    backgroundColor: COLORS.DRIFT_WOOD,
    color: COLORS.DRIFT_WOOD,
  },
  "&.TAG_YEllOW": {
    backgroundColor: COLORS.TAG_YEllOW,
    color: COLORS.TAG_YEllOW,
  },
  "&.PERSIAN_GREEN_DARK": {
    backgroundColor: COLORS.PERSIAN_GREEN_DARK,
    color: COLORS.PERSIAN_GREEN_DARK,
  },
  "&.PARTIAL_SUCCESS_GREEN": {
    backgroundColor: COLORS.PARTIAL_SUCCESS_GREEN,
    color: COLORS.PARTIAL_SUCCESS_GREEN,
  },
  "&.SILVER_CHALICE2": {
    backgroundColor: COLORS.SILVER_CHALICE2,
    color: COLORS.SILVER_CHALICE2,
  },
  "&.PALE_RED": {
    backgroundColor: COLORS.PALE_RED,
    color: COLORS.PALE_RED,
  },
  "&.GREEN_BLUE": {
    backgroundColor: COLORS.GREEN_BLUE,
    color: COLORS.GREEN_BLUE,
  },
  "&.MAROON": {
    backgroundColor: COLORS.MAROON,
    color: COLORS.MAROON,
  },
  "&.BLUE_HAZE": {
    color: COLORS.BLUE_HAZE,
    backgroundColor: COLORS.BLUE_HAZE_LIGHT,
  },
  "&.ALTO": {
    color: COLORS.ALTO,
  },
  "&.CATSKILL_WHITE": {
    backgroundColor: COLORS.CATSKILL_WHITE,
    color: COLORS.ASTRAL,
  },
  "&.BARLEY_WHITE": {
    backgroundColor: COLORS.BARLEY_WHITE,
    color: COLORS.OLD_GOLD,
  },
  "&.PICTON_BLUE": {
    color: COLORS.PICTON_BLUE,
    backgroundColor: COLORS.PICTON_BLUE_LIGHT,
  },
  "&.CREAM_CAN": {
    color: COLORS.CREAM_CAN,
    backgroundColor: COLORS.CREAM_CAN_LIGHT,
  },
  "&.BLUE": {
    color: COLORS.PRIMARY_MAIN,
    backgroundColor: COLORS.PRIMARY_LIGHT,
  },
  "&.VISTA_BLUE": {
    color: COLORS.VISTA_BLUE,
    backgroundColor: COLORS.VISTA_BLUE_LIGHT,
  },
  "&.SEA_GREEN": {
    color: COLORS.SEA_GREEN,
    backgroundColor: COLORS.SEA_GREEN_LIGHT,
  },
  "&.OLD_GOLD": {
    color: COLORS.OLD_GOLD,
    backgroundColor: COLORS.OLD_GOLD_LIGHT,
  },
  "&.SEA_GREEN2": {
    color: COLORS.SEA_GREEN2,
    backgroundColor: COLORS.SEA_GREEN2_LIGHT,
  },
  "&.LUCKY": {
    color: COLORS.LUCKY,
    backgroundColor: COLORS.LUCKY_LIGHT,
  },
  "&.DUSTY_GRAY": {
    color: COLORS.DUSTY_GRAY,
    backgroundColor: COLORS.DUSTY_GRAY_NEW_LIGHT,
  },
  "&.JUNGLE_GREEN": {
    color: COLORS.JUNGLE_GREEN,
    backgroundColor: COLORS.JUNGLE_GREEN_LIGHT,
  },
  "&.BLACK": {
    color: COLORS.BLACK,
    backgroundColor: COLORS.BLACK_LIGHT,
  },
};
